import { fetchSeguro } from '../../../../lib/fetchSeguro';
import { EvidenciaImg } from '../../../../types/ot.types';
import {
    CatalogoItem,
    CategoriaOption,
    CentroDetailResponse,
    CentroFormData,
    CentrosListResponse,
    CiudadOption,
    DetalleOption,
    MarcaOption,
    ModeloOption,
    ProveedorOption,
    Slot,
    SubcategoriaOption,
    TipoCentroOption,
} from '../types/centro.types';

interface ApiListResponse<T> {
    status?: string;
    data?: T;
}

export class CentroService {
    static async fetchCentros(params: {
        proveedorId?: string | number;
        searchTerm: string;
        page: number;
        limit: number;
        sortBy: string;
    }): Promise<CentrosListResponse> {
        return fetchSeguro<CentrosListResponse>('/sittApi/obtener_centros.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                proveedorId: params.proveedorId || null,
                searchTerm: params.searchTerm,
                page: params.page,
                limit: params.limit,
                sortBy: params.sortBy,
            }),
        });
    }

    static async obtenerCentro(centroIdEnc: string, idProveedorEnc: string): Promise<CentroDetailResponse> {
        return fetchSeguro<CentroDetailResponse>(
            `/sittApi/centros/obtener.php?id=${centroIdEnc}&idProveedor=${idProveedorEnc}`,
        );
    }

    static async guardarCentro(endpoint: string, payload: FormData): Promise<{ status: string; message?: string; data?: { id_encriptado_prov: string } }> {
        return fetchSeguro(endpoint, { method: 'POST', body: payload });
    }

    static async cambiarEstatus(body: {
        id: string | null;
        idProveedor: string;
        estatus: string;
        comentario: string;
    }): Promise<{ status: string; message?: string }> {
        return fetchSeguro('/sittApi/centros/cambiar_estatus.php', {
            method: 'POST',
            body: JSON.stringify(body),
        });
    }

    static async loadCatalogosIniciales(idProveedorEnc: string | null) {
        const [est, prove, prov, cat, marca, tipos, catBancos, archivosRes] = await Promise.all([
            fetchSeguro<ApiListResponse<unknown[]>>('/sittApi/obtener_estados.php'),
            idProveedorEnc
                ? fetchSeguro<ApiListResponse<ProveedorOption>>(`/sittApi/proveedores/obtener.php?id=${idProveedorEnc}`)
                : Promise.resolve({ data: null }),
            fetchSeguro<ApiListResponse<ProveedorOption[]>>('/sittApi/obtener_proveedores.php', {
                method: 'POST',
                body: JSON.stringify({ limit: 9999 }),
            }),
            fetchSeguro<ApiListResponse<CategoriaOption[]>>('/sittApi/obtener_categorias.php'),
            fetchSeguro<MarcaOption[]>('/api/catalogos/marcas.php?accion=filtered'),
            fetchSeguro<ApiListResponse<TipoCentroOption[]>>('/sittApi/catalogos/tipos_centro.php').catch(() => ({ data: [] })),
            fetchSeguro<ApiListResponse<CatalogoItem[]>>('/sittApi/catalogos/bancos.php').catch(() => ({ data: [] })),
            idProveedorEnc
                ? fetchSeguro<ApiListResponse<{ archivos: { contexto: string; clave_centro: string | number }[] }>>(
                      '/sittApi/proveedores/obtener_archivos.php',
                      {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ clave_persona_proveedor: idProveedorEnc }),
                      },
                  )
                : Promise.resolve({ status: 'success', data: { archivos: [] } }),
        ]);

        return {
            estados: (est.data ?? []) as import('../types/centro.types').EstadoOption[],
            proveedor: prove.data ?? null,
            proveedores: prov.data ?? [],
            categorias: cat.data ?? [],
            marcas: Array.isArray(marca) ? marca : [],
            tiposCentro: tipos.data ?? [],
            bancos: catBancos.data ?? [],
            archivosRes,
        };
    }

    static async loadCiudades(claveEstado: string): Promise<CiudadOption[]> {
        const res = await fetchSeguro<CiudadOption[]>('/api/catalogos/ciudades.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({ accion: 'filtered', idEstado: claveEstado }),
        });
        return Array.isArray(res) ? res : [];
    }

    static async loadSubcategorias(idCategoria: string): Promise<SubcategoriaOption[]> {
        const res = await fetchSeguro<ApiListResponse<SubcategoriaOption[]> | SubcategoriaOption[]>(
            '/api/catalogos/subcategorias.php',
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({ accion: 'filtered', idCategoria }),
            },
        );
        if (Array.isArray(res)) return res;
        return res.data ?? [];
    }

    static async loadDetalles(idCategoria: string, idSubcategoria: string): Promise<DetalleOption[]> {
        const res = await fetchSeguro<ApiListResponse<DetalleOption[]> | DetalleOption[]>(
            '/api/catalogos/subcategoria_detalles.php',
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({ accion: 'filtered', idCategoria, idSubcategoria }),
            },
        );
        if (Array.isArray(res)) return res;
        return res.data ?? [];
    }

    static async loadModelos(idMarca: string): Promise<ModeloOption[]> {
        const res = await fetchSeguro<ModeloOption[]>(`/api/catalogos/modelos.php?accion=filtered&idMarca=${btoa(idMarca)}`);
        return Array.isArray(res) ? res : [];
    }

    static buildFormPayload(
        formData: CentroFormData,
        slots: Slot[],
        constanciaFiles: EvidenciaImg[],
        caratulaFiles: EvidenciaImg[],
    ): FormData {
        const payload = new FormData();

        // Objetos (horarios, servicios, expediente) van como JSON; el PHP hace json_decode
        Object.keys(formData).forEach((key) => {
            const value = formData[key as keyof CentroFormData];
            if (typeof value === 'object' && value !== null) {
                payload.append(key, JSON.stringify(value));
            } else {
                payload.append(key, value === null || value === undefined ? '' : String(value));
            }
        });

        payload.append('slots', JSON.stringify(slots));

        // guardarCentro usa fetch sin Content-Type para que el browser arme el boundary multipart
        if (constanciaFiles.length > 0 && constanciaFiles[0].file) {
            payload.append('tax_certificate', constanciaFiles[0].file, constanciaFiles[0].nombreOriginal);
        }
        if (caratulaFiles.length > 0 && caratulaFiles[0].file) {
            payload.append('bank_cover', caratulaFiles[0].file, caratulaFiles[0].nombreOriginal);
        }

        return payload;
    }
}
